import { api, defineWebModule } from '@itc/sdk';
import { getOpCounts, searchOps } from '../shared/endpoints.ts';
import { ALL_TOPIC } from '../shared/topics.ts';

const drafts = () => import('./reports.ts');
const images = () => import('./cards.ts');

// Ops in the shell (design §7.5, plan 2D): the Ops page, with its board, the open op
// and the Schedule; People and the scorecard; published reports (plan 3C); the events calendar (plan 5F); the Next op
// widget on Home; and a badge for those who deposit.
// Members with ops.view_schedule see upcoming ops and nothing more: the server decides.
// Its reports load only when drafted (ADR 0015).
export default defineWebModule({
  name: 'ops',
  title: 'Ops',
  search: { permissions: ['ops.view', 'ops.view_schedule', 'ops.view_people', 'ops.view_reports'], run: async (q, signal) => (await api(searchOps, { query: { q } }, { signal })).hits },
  routes: [
    { path: '/ops', component: () => import('./pages/OpsPage.vue'), meta: { permission: 'ops.view_schedule', title: 'Ops' } },
    { path: '/ops/people', component: () => import('./pages/PeoplePage.vue'), meta: { permission: 'ops.view_people', title: 'People' } },
    { path: '/ops/reports', component: () => import('./pages/ReportsPage.vue'), meta: { permission: 'ops.view_reports', title: 'Reports' } },
    // Every guild event, in the viewer's own zone (plan 5F). A button's link from Discord opens it with `?view=`.
    { path: '/ops/calendar', component: () => import('./pages/CalendarPage.vue'), meta: { permission: 'ops.view', title: 'Calendar' } },
  ],
  admin: [
    { label: 'Report templates', path: '/admin/ops/templates', component: () => import('./components/ReportTemplates.vue'), permission: 'ops.manage_reports' },
    { label: 'Calendar posts', path: '/admin/ops/calendar', component: () => import('./components/CalendarAdmin.vue'), permission: 'ops.admin' },
  ],
  nav: [
    {
      label: 'Ops',
      to: '/ops',
      permission: 'ops.view_schedule',
      icon: 'ops',
      // R17: earnings waiting on the viewer, re-read on ops.all changes only.
      badge: {
        permission: 'ops.deposit',
        count: async () => (await api(getOpCounts)).deposits,
        topics: (_userId, can) => (can('ops.view') ? [ALL_TOPIC] : []),
        label: (count) => `${String(count)} earnings waiting`,
      },
    },
    { label: 'People', to: '/ops/people', permission: 'ops.view_people', icon: 'people' },
    { label: 'Reports', to: '/ops/reports', permission: 'ops.view_reports', icon: 'library' },
    { label: 'Calendar', to: '/ops/calendar', permission: 'ops.view', icon: 'timers' },
  ],
  widgets: [{ id: 'ops.next', title: 'Next op', permission: 'ops.view_schedule', component: () => import('./widgets/NextOpWidget.vue') }],
  cards: [
    { id: 'ops.weekly', label: 'Weekly post', permission: 'ops.view_people', component: () => import('./components/WeeklySheet.vue') },
    { id: 'ops.monthly', label: 'Monthly report', permission: 'ops.view_people', spec: async () => (await images()).monthlyImage() },
    { id: 'ops.schedule', label: 'Ops schedule', permission: 'ops.view_schedule', spec: async () => (await images()).scheduleImage() },
    {
      id: 'ops.score',
      label: 'Scorecard',
      permission: 'ops.view_people',
      variants: [
        { value: 'table', label: 'Scorecard' },
        { value: 'podium', label: 'Top 3' },
      ],
      spec: async (variant) => (await images()).scoreImage('30', variant),
    },
  ],
  reports: [
    { type: 'aar', permission: 'ops.view', choose: async () => (await drafts()).chooseOp(), draft: async (op) => (await drafts()).aar(op) },
    { type: 'haul', permission: 'ops.view', draft: async () => (await drafts()).haul() },
    { type: 'battle', permission: 'ops.view', draft: async () => (await drafts()).battle() },
    { type: 'scorecard', permission: 'ops.view_people', draft: async () => (await drafts()).scorecard() },
    { type: 'schedule', permission: 'ops.view_schedule', draft: async () => (await drafts()).schedule() },
    { type: 'attendance', permission: 'ops.view_people', draft: async () => (await drafts()).attendance() },
  ],
});
